import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    InputLabel,
    MenuItem,
    Select,
} from '@mui/material';
import { StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider } from '@mui/material/styles';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import MaintenancePage from '../Components/503';
import StatusAlert from '../Components/StatusAlert';
import Routes from '../Routes';
import { HOSTNAME_IS_DEV, HOSTNAME_IS_STAGING, HOSTNAME_IS_TEST } from '../constants/environment';
import { AlertContext, DomainContext, UserContext } from '../context';

import { fetchOnboardingTasksOperation } from '../redux/SignUpFlow/SignUpFlowOperations';

const PICK_ENV = (() => {
    if (HOSTNAME_IS_DEV) return ' (dev)';
    if (HOSTNAME_IS_TEST) return ' (test)';
    if (HOSTNAME_IS_STAGING) return ' (stage)';
    return '';
})();

const AppRenderer: React.FC = () => {
    const {
        theme,
        domainOptions,
        selectedDomain,
        handleSetDomain,
        handleDomainSelectClose,
        handleDomainSelectChange,
        domainData,
        serverError,
    } = React.useContext(DomainContext) || {};
    const { alert, handleClose } = React.useContext(AlertContext);
    const { fetchedUser, activeClient } = React.useContext(UserContext);
    const dispatch = useDispatch();
    const faviconUrl = domainData?.domainUISetting?.faviconUrl;
    const title = domainData?.domainUISetting?.title;

    React.useEffect(() => {
        if (fetchedUser && !!theme && !!domainData) {
            const tag = document.getElementById('favicon') as HTMLLinkElement;
            if (faviconUrl) tag.href = faviconUrl;
            document.title = `${title}${PICK_ENV}`;
        } else {
            document.title = 'Welcome Back';
        }
    }, [fetchedUser, theme, faviconUrl, title, domainData]);

    React.useEffect(() => {
        if (activeClient) fetchOnboardingTasksOperation(dispatch);
    }, [activeClient, dispatch]);

    if (!theme && domainOptions) {
        return domainOptions?.length ? (
            <Dialog open onClose={handleDomainSelectClose}>
                <DialogTitle>Select App Domain</DialogTitle>
                <DialogContent>
                    <InputLabel id='client-select-label'>Domains</InputLabel>
                    <Select
                        variant='standard'
                        value={selectedDomain}
                        onChange={handleDomainSelectChange}
                        fullWidth
                    >
                        <MenuItem value=''>
                            <em>Select a domain</em>
                        </MenuItem>
                        {domainOptions.map((option) => (
                            <MenuItem key={option.id} value={option.domain}>
                                {option.domain}
                            </MenuItem>
                        ))}
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDomainSelectClose} color='secondary'>
                        Cancel
                    </Button>
                    <Button onClick={handleSetDomain} color='secondary'>
                        Select
                    </Button>
                </DialogActions>
            </Dialog>
        ) : null;
    }
    if (!domainData && serverError) {
        return <MaintenancePage />;
    }
    if (!domainData || !theme) return null;

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <Routes />
                <StatusAlert
                    style={alert.style}
                    open={alert.open}
                    severity={alert.severity}
                    alertTitle={alert.alertTitle}
                    message={alert.message}
                    handleClose={handleClose}
                    autoHideDuration={alert.autoHideDuration}
                    isModal={alert.isModal}
                />
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

export default AppRenderer;
